/**
 * One simulation tick. Reads `prev` and the input, writes `next`, appends
 * events. Allocation-free: every scratch value is a local number or a field
 * on a preallocated object.
 */

import { integrateAttitude, integrateRoll, pinAttitudeToBar, updateInertia } from "./attitude";
import { comHeight, updateArms, updateTuck } from "./body";
import { moveResult } from "./collision";
import type { EventBuffer } from "./events";
import { linkOnRestart } from "./link";
import { moveAndGround, tickAirborne, tryJump } from "./moves/air";
import { tickAttached, tickLockout, tryGrip } from "./moves/bar";
import { checkWater, respawn, trackProgress } from "./moves/fail";
import { tickGrounded } from "./moves/ground";
import { afterMoveHurdles, armVault, groundHurdles, tickPlanted } from "./moves/hurdles";
import { tickRolling, trackApex } from "./moves/landing";
import { tickLedge, tickLedgeLockout, tryLedge } from "./moves/ledge";
import type { SimState } from "./state";
import { trackRun } from "./timing";
import type { CompiledTuning } from "./tuning";
import { type InputFrame, Run, S } from "./types";
import type { World } from "./world";

export function step(
  world: World,
  prev: SimState,
  next: SimState,
  input: InputFrame,
  t: CompiledTuning,
  ev: EventBuffer,
): void {
  ev.clear();
  next.copyFrom(prev);
  const s = next;
  s.tick = prev.tick + 1;

  const jumpPressed = input.jump && prev.prevJump === 0;
  const tuckPressed = input.tuck && prev.prevTuck === 0;
  const tuckReleased = !input.tuck && prev.prevTuck === 1;
  const pullHeld = input.pull >= t.ladderPullThreshold;
  const pullPressed = pullHeld && prev.prevPull === 0;
  const restartPressed = input.restart && prev.prevRestart === 0;

  // Hit-stop: the world is frozen. Only the freeze counter and the jump buffer move, no window's
  // clock runs, and nothing is emitted; restart still works.
  if (s.freezeTicks > 0 && !restartPressed) {
    s.freezeTicks--;
    if (jumpPressed) s.buffer = t.bufferTicks;
    s.ax = 0;
    s.ay = 0;
    s.prevJump = input.jump ? 1 : 0;
    s.prevTuck = input.tuck ? 1 : 0;
    s.prevPull = pullHeld ? 1 : 0;
    s.prevRestart = 0;
    s.prevPause = input.pause ? 1 : 0;
    return;
  }

  s.stateTicks++;
  if (s.ticksSinceLand < 1e9) s.ticksSinceLand++;
  if (s.ticksSinceRelease < 1e9) s.ticksSinceRelease++;
  s.reach = input.reach;
  s.pull = input.pull;
  const respawnBefore = s.respawnIndex;
  let teleported = false;

  if (restartPressed) {
    linkOnRestart(s, ev);
    s.freezeTicks = 0;
    // A restart on a timed course goes back to the warm-up area; in the gym it is the last respawn point.
    if (world.timed) s.respawnIndex = 0;
    respawn(world, s, t, ev);
    teleported = true;
  }

  if (s.state === S.Fail) {
    if (s.failTicks > 0) s.failTicks--;
    if (s.failTicks <= 0) {
      respawn(world, s, t, ev);
      teleported = true;
    }
  } else {
    const tuckBefore = s.tuck;
    if (s.barId < 0 && s.grounded === 1 && s.tumbling === 0) armVault(world, s, t, ev, tuckPressed);
    updateTuck(world, s, input, t);
    updateInertia(s, t);

    const wasAttached = s.barId >= 0;
    const wasOnLedge = s.ledgeSolid >= 0;
    if (wasAttached) {
      tickAttached(world, s, input, t, ev, jumpPressed, tuckPressed, tuckReleased, pullPressed);
      // Still on the bar the body hangs on the arm line; released this tick it flies with the carried spin.
      if (s.barId >= 0) pinAttitudeToBar(s, input, t);
      else integrateAttitude(s, input, t, false);
      s.collided = 0;
    } else if (wasOnLedge) {
      // Hanging from a ledge: the hang holds the body (upright, kinematic); a drop or a mantle this
      // tick leaves the body where it is and the next tick moves it.
      tickLedge(world, s, t, ev, jumpPressed, pullPressed);
      s.collided = 0;
    } else {
      const wasAirborne = s.grounded === 0;
      // In the air the mass holds still through a change of shape, so the feet take the difference.
      const shapeDy = wasAirborne ? comHeight(t, tuckBefore) - comHeight(t, s.tuck) : 0;
      const jumped = tryJump(s, input, t, ev, jumpPressed);
      if (!jumped && s.grounded === 1 && s.tumbling === 0) groundHurdles(world, s, input, t, ev, tuckPressed);
      const rolling = s.tumbling === 1 && s.grounded === 1 && !jumped;
      if (rolling) tickRolling(s, ev, integrateRoll(s, t), input.moveX !== 0);
      else integrateAttitude(s, input, t, s.grounded === 1);
      if (s.grounded === 1 && !jumped) {
        if (!rolling) tickGrounded(s, input, t, ev);
      } else if (s.planted !== 0) tickPlanted(world, s, t);
      else tickAirborne(s, input, t, ev);
      moveAndGround(world, s, input, t, ev, jumpPressed, shapeDy);
      afterMoveHurdles(world, s, t, ev, moveResult.wallSolid, moveResult.wallSpeed);
      // A roll that leaves the ground (an edge) flies on with its spin.
      if (s.grounded === 0) s.tumbling = 0;
      // Forgiveness counters tick down after the checks, so a window of N ticks honors N presses.
      if (!jumped && wasAirborne && s.grounded === 0 && s.coyote > 0) s.coyote--;
      if (s.buffer > 0 && !jumpPressed) s.buffer--;
    }

    updateArms(world, s, t);
    if (s.barId < 0) {
      if (s.armsOut === 1) {
        if (s.armsOutTicks < 1e6) s.armsOutTicks++;
      } else s.armsOutTicks = 0;
    }
    if (s.barId < 0 && !wasAttached && s.ledgeSolid < 0) {
      tickLockout(world, s);
      if (tryGrip(world, s, t, ev)) updateArms(world, s, t);
    }
    // A ledge's corner is taken by the same hands, once no bar has been: the nearer grip wins by order.
    if (s.ledgeSolid < 0 && !wasOnLedge) {
      tickLedgeLockout(s);
      if (s.barId < 0 && tryLedge(world, s, t, ev)) updateArms(world, s, t);
    }
    if (checkWater(world, s, t, ev) && s.run === Run.Running) s.runFalls++;
    trackProgress(world, s, ev);
    trackApex(s, t);
  }
  trackRun(world, s, ev, prev.x, respawnBefore, teleported, restartPressed);

  updateArms(world, s, t);
  s.prevJump = input.jump ? 1 : 0;
  s.prevTuck = input.tuck ? 1 : 0;
  s.prevPull = pullHeld ? 1 : 0;
  s.prevRestart = input.restart ? 1 : 0;
  s.prevPause = input.pause ? 1 : 0;
}
